import type { ReactNode, RefObject } from "react";
import {
  FiChevronDown,
  FiLoader,
  FiRefreshCw,
  FiSettings,
  FiZap,
} from "react-icons/fi";
import SafeIcon from "./SafeIcon";
import type { ConverterTheme, FormatOption } from "./types";

interface ConverterToolbarProps<T extends string> {
  fileCount: number;
  convertedCount: number;
  targetFormat: T;
  formatOptions: Array<FormatOption<T>>;
  onFormatChange: (format: T) => void;
  isConverting: boolean;
  progress: number;
  mediaLabel: string;
  settingsOpen: boolean;
  hasCustomSettings: boolean;
  onToggleSettings: () => void;
  onReset: () => void;
  onConvert: () => void;
  settingsButtonRef: RefObject<HTMLButtonElement | null>;
  settingsPanel: ReactNode;
  theme: ConverterTheme;
}

const ConverterToolbar = <T extends string>({
  fileCount,
  convertedCount,
  targetFormat,
  formatOptions,
  onFormatChange,
  isConverting,
  progress,
  mediaLabel,
  settingsOpen,
  hasCustomSettings,
  onToggleSettings,
  onReset,
  onConvert,
  settingsButtonRef,
  settingsPanel,
  theme,
}: ConverterToolbarProps<T>) => (
  <div
    className={`glass relative z-10 flex flex-col items-start justify-between gap-4 rounded-3xl p-5 shadow-xl md:flex-row md:items-center md:p-6 ${
      settingsOpen ? "z-[70]" : ""
    }`}
  >
    <div>
      <h2 className="text-lg font-extrabold text-slate-800 dark:text-white">
        {convertedCount
          ? `${convertedCount} file${convertedCount > 1 ? "s" : ""} ready to download`
          : `${fileCount} file${fileCount > 1 ? "s" : ""} queued`}
      </h2>
      <p className="mt-0.5 text-xs text-slate-500">
        All processing is 100% local. Your files never leave your device.
      </p>
    </div>

    <div className="flex w-full flex-wrap items-center gap-3 md:w-auto">
      {!convertedCount && (
        <>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-800">
            <SafeIcon icon={FiSettings} className="text-slate-400" />
            <select
              value={targetFormat}
              onChange={(e) => onFormatChange(e.target.value as T)}
              disabled={isConverting}
              aria-label="Output format"
              className={`cursor-pointer rounded bg-white text-sm font-bold text-slate-700 outline-none focus-visible:ring-2 ${theme.focusRing} disabled:cursor-not-allowed dark:bg-slate-800 dark:text-slate-200`}
            >
              {formatOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  title={option.disabledReason}
                >
                  {option.disabled && option.disabledLabel
                    ? option.disabledLabel
                    : option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative z-[80]">
            <button
              ref={settingsButtonRef}
              type="button"
              onClick={onToggleSettings}
              className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-bold transition-[background-color,border-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} ${
                hasCustomSettings
                  ? theme.settingsActiveButton
                  : "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              }`}
              aria-expanded={settingsOpen}
              aria-label="Conversion settings"
            >
              <SafeIcon icon={FiSettings} />
              <span>Settings</span>
              {hasCustomSettings && (
                <span
                  className={`h-2 w-2 animate-pulse rounded-full ${theme.settingsDot}`}
                />
              )}
              <SafeIcon
                icon={FiChevronDown}
                className={`text-xs transition-transform ${settingsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {settingsPanel}
          </div>
        </>
      )}

      {convertedCount > 0 ? (
        <button
          type="button"
          onClick={onReset}
          className={`flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-100 px-5 py-2.5 text-sm font-bold text-slate-700 transition-[background-color,border-color,color,transform] hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700`}
        >
          <SafeIcon icon={FiRefreshCw} />
          Convert More
        </button>
      ) : (
        <button
          type="button"
          onClick={onConvert}
          disabled={isConverting || !fileCount}
          aria-busy={isConverting}
          className={`flex min-w-[170px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r px-6 py-2.5 text-sm font-bold text-white shadow-lg transition-[background-color,box-shadow,transform,opacity] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 ${theme.focusRing} focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950 ${theme.primaryButton} ${theme.primaryShadow}`}
        >
          {isConverting ? (
            <>
              <SafeIcon icon={FiLoader} className="animate-spin" />
              {progress}% Converting...
            </>
          ) : (
            <>
              <SafeIcon icon={FiZap} />
              Convert {fileCount} {mediaLabel}
              {fileCount > 1 ? "s" : ""}
            </>
          )}
        </button>
      )}
    </div>
  </div>
);

export default ConverterToolbar;
